// core
import { Component, Renderer2, Inject, OnInit } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { environment } from '@open-portal-env';
import { datadogRum } from '@datadog/browser-rum';

// campfire
import {
  ConfigService,
  LoggerService,
  PartnerBrandService,
  ScriptLoaderService
} from '@campfire-nx/api';

//  constants
import { SCRIPTS_CONSTANTS } from './core/constants/scripts.contants';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  title = 'open-portal';

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    private configService: ConfigService,
    private partnerBrandService: PartnerBrandService,
    private loggerService: LoggerService,
    private scriptLoaderService: ScriptLoaderService
  ) {

    if (environment.env !== 'local') {
      datadogRum.init({
        applicationId: environment.datadogApplicationId,
        clientToken: environment.datadogClientToken,
        site: environment.datadogSite,
        service: environment.datadogService,
        env: environment.env,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        version: environment.version,
        allowedTracingUrls: [
          { match: (url: string) => url.startsWith(environment.apiEndpoint), propagatorTypes: ['datadog']}
        ]
      });
    }

    this.configService.portal()
      .then((data: any) => {
        window.sessionStorage.setItem('config', JSON.stringify(data));
        this.activate(data);
      });
  }

  ngOnInit() {
    this.loadThirdPartyScripts();
  }

  loadThirdPartyScripts() {
    // Renders all scripts in SCRIPTS_CONSTANTS.thirdPartyScripts
    for (const value of SCRIPTS_CONSTANTS.thirdPartyScripts) {
      let scriptText;
      if (typeof value.scriptText === 'string') {
        scriptText = value.scriptText;
      } else {
        // Use current region's script or fallback to 'au' script if undefined
        scriptText = (value.scriptText as { [key: string]: string })[environment.region] || value.scriptText.au;
      }

      if (scriptText) {
        this.scriptLoaderService.loadJsScript(
          this.renderer,
          scriptText,
          value.scriptType
        );
      }
    }

    if (environment.region === 'uk') {
      let accountNumber;
      // TODO: the cookie yes configuration needs to be added to the partner config
      // supplied by the BE eventually OR this value should be set in the pipeline
      if (window.location.host.indexOf('sosure') > -1) {
        accountNumber = '470c93b9f04d66c31635c31d';
      } else if (window.location.host.indexOf('beopen') > -1) {
        accountNumber = '54e8a2af9b79a9ba18aa7d1f';
      } else if (window.location.host.indexOf('huddleinsurance') > -1) {
        accountNumber = 'f3109204a4c4233780752ef8';
      } else if (window.location.host.indexOf('wanda') > -1) {
        accountNumber = 'a9160874a382fccaa46ef791';
      }
      if (accountNumber) {
        const cookieyes = this.renderer.createElement('script');
        cookieyes.src = `https://cdn-cookieyes.com/client_data/${accountNumber}/script.js`;
        cookieyes.id = 'cookieyes';
        cookieyes.type = 'text/javascript';
        this.renderer.appendChild(this.document.body, cookieyes);
      }
    }
  }

  private activate(config: any) {
    this.initThirdPartyServices(config);
    this.loggerService.initiateLogging(environment.datadogService);
  }

  private initThirdPartyServices(config: any) {
    // Load segment.io tag
    if (config.segmentID) {
      const segment = this.renderer.createElement('script');
      segment.text = `window.dataLayer=window.dataLayer||[];(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({"gtm.start":new Date().getTime(),event:"gtm.js"});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!="dataLayer"?"&l="+l:"";j.async=true;j.src="https://www.googletagmanager.com/gtm.js?id="+i+dl;f.parentNode.insertBefore(j,f);})(window,document,"script","dataLayer","${config.segmentID}");`;
      this.renderer.appendChild(this.document.body, segment);
    }

    // partner service now manages the partner scripts
    this.partnerBrandService.addPartnerScripts();

    if (config.debugEnabled) return;
  }
}
